'use client';

import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import EventEmitter from '../../_ui/shared/event-emitter';
import IdleTimer from './idle-timer';

const DEFAULT_IDLE_TIMEOUT_MS = 1_800_000; // 30 mins
const IsIdleTimerProviderMountedContext = createContext(false);
const idleEventEmitter = new EventEmitter<void>();
const activeAfterIdleEventEmitter = new EventEmitter<void>();

export function useOnIdle(callback: () => void) {
  const isIdleTimerProviderMounted = useContext(
    IsIdleTimerProviderMountedContext,
  );

  useEffect(() => {
    const unsubscribe = isIdleTimerProviderMounted
      ? idleEventEmitter.subscribe(callback)
      : null;

    return () => unsubscribe?.();
  }, [callback, isIdleTimerProviderMounted]);

  if (!isIdleTimerProviderMounted) {
    throw new Error('useOnIdle has to be used within <IdleTimeProvider>');
  }
}

export function useOnActiveAfterIdle(callback: () => void) {
  const isIdleTimerProviderMounted = useContext(
    IsIdleTimerProviderMountedContext,
  );

  useEffect(() => {
    const unsubscribe = isIdleTimerProviderMounted
      ? activeAfterIdleEventEmitter.subscribe(callback)
      : null;

    return () => unsubscribe?.();
  }, [callback, isIdleTimerProviderMounted]);

  if (!isIdleTimerProviderMounted) {
    throw new Error(
      'useOnActiveAfterIdle has to be used within <IdleTimeProvider>',
    );
  }
}

export function useIsIdle() {
  const [isIdle, setIsIdle] = useState(false);

  useOnIdle(useCallback(() => setIsIdle(true), []));
  useOnActiveAfterIdle(useCallback(() => setIsIdle(false), []));

  return isIdle;
}

interface Props {
  timeoutMs?: number;
}

export default function IdleTimeProvider({
  children,
  timeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
}: PropsWithChildren<Props>) {
  useEffect(() => {
    const idleTimer = new IdleTimer({
      timeoutMs,
      onIdle: () => idleEventEmitter.emit(),
      onActiveAfterIdle: () => activeAfterIdleEventEmitter.emit(),
    });

    return () => idleTimer.cleanUp();
  }, [timeoutMs]);

  return (
    <IsIdleTimerProviderMountedContext.Provider value={true}>
      {children}
    </IsIdleTimerProviderMountedContext.Provider>
  );
}
